import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    buildDefaultTableColumnOrder,
    buildDefaultTableVisibleColumnsMap,
    buildTableColumnsSignature,
    normalizeTableColumnDefinitionsForPreferences,
    getEffectiveTableColumnOrder,
    getOrderedTableColumnDefinitions,
    getTableColumnPreferencesStorageKey,
    getVisibleTableColumnOrder,
    loadTableColumnPreferencesFromStorage,
    mergeTableColumnOrderWithDefinitions,
    mergeTableVisibleColumnsWithDefinitions,
    reorderVisibleTableColumnOrder,
    resolveTableColumnPreferences,
    saveTableColumnPreferencesToStorage,
} from "helpers/GeneralHelper";
import {
    saveTableColumnPreferencesUserSetting,
} from "model/UserSettingModel";
import { useUserSettingsSync } from "libs/context/HeaderSettingsSyncContext";

const TABLE_COLUMN_PREFS_SYNC_DEBOUNCE_MS = 400;

const TableColumnPreferencesContext = createContext(null);

const EMPTY_PREFERENCES = {
    columnOrder: [],
    visibleColumns: {},
};

export const TableColumnPreferencesProvider = ({ children }) => {
    const { syncGeneration } = useUserSettingsSync();
    const [preferencesByTableKey, setPreferencesByTableKey] = useState({});
    const serverSyncTimeoutByTableKeyRef = useRef({});

    useEffect(
        () => () => {
            Object.values(serverSyncTimeoutByTableKeyRef.current).forEach(clearTimeout);
            serverSyncTimeoutByTableKeyRef.current = {};
        },
        []
    );

    /** Re-read localStorage after the shared UserSetting sync. */
    useEffect(() => {
        if (syncGeneration < 1) return;
        setPreferencesByTableKey({});
    }, [syncGeneration]);

    const syncPreferencesToServer = useCallback((tableKey, preferences) => {
        if (!tableKey) return;

        const timeouts = serverSyncTimeoutByTableKeyRef.current;
        if (timeouts[tableKey]) {
            clearTimeout(timeouts[tableKey]);
        }

        timeouts[tableKey] = setTimeout(() => {
            delete timeouts[tableKey];
            saveTableColumnPreferencesUserSetting(
                getTableColumnPreferencesStorageKey(tableKey),
                preferences
            );
        }, TABLE_COLUMN_PREFS_SYNC_DEBOUNCE_MS);
    }, []);

    const persistPreferences = useCallback(
        (tableKey, preferences, { syncToServer = true } = {}) => {
            if (!tableKey) return;
            saveTableColumnPreferencesToStorage(tableKey, preferences);
            setPreferencesByTableKey((previous) => ({
                ...previous,
                [tableKey]: preferences,
            }));
            if (syncToServer) {
                syncPreferencesToServer(tableKey, preferences);
            }
        },
        [syncPreferencesToServer]
    );

    const syncTableColumnsFromDefinitions = useCallback((tableKey, columns = []) => {
        if (!tableKey) return EMPTY_PREFERENCES;

        const stored = loadTableColumnPreferencesFromStorage(tableKey);
        const resolved = resolveTableColumnPreferences(tableKey, columns, stored);
        const next = {
            columnOrder: mergeTableColumnOrderWithDefinitions(
                resolved.columnOrder,
                columns
            ),
            visibleColumns: mergeTableVisibleColumnsWithDefinitions(
                resolved.visibleColumns,
                columns
            ),
        };

        saveTableColumnPreferencesToStorage(tableKey, next);
        setPreferencesByTableKey((previous) => ({
            ...previous,
            [tableKey]: next,
        }));

        return next;
    }, []);

    const getTablePreferences = useCallback(
        (tableKey, columns = []) => {
            if (!tableKey) {
                return {
                    columnOrder: buildDefaultTableColumnOrder(columns),
                    visibleColumns: buildDefaultTableVisibleColumnsMap(columns),
                };
            }
            const fromMemory = preferencesByTableKey[tableKey];
            if (fromMemory) {
                return {
                    columnOrder: getEffectiveTableColumnOrder(fromMemory.columnOrder, columns),
                    visibleColumns: mergeTableVisibleColumnsWithDefinitions(
                        fromMemory.visibleColumns,
                        columns
                    ),
                };
            }
            return resolveTableColumnPreferences(tableKey, columns);
        },
        [preferencesByTableKey]
    );

    const setTableColumnOrder = useCallback(
        (tableKey, columnOrder, columns = []) => {
            if (!tableKey) return;
            const current = getTablePreferences(tableKey, columns);
            persistPreferences(tableKey, {
                ...current,
                columnOrder: Array.isArray(columnOrder) ? columnOrder : [],
            });
        },
        [getTablePreferences, persistPreferences]
    );

    const setTableVisibleColumns = useCallback(
        (tableKey, visibleColumns, columns = []) => {
            if (!tableKey) return;
            const current = getTablePreferences(tableKey, columns);
            persistPreferences(tableKey, {
                ...current,
                visibleColumns:
                    visibleColumns && typeof visibleColumns === "object"
                        ? visibleColumns
                        : {},
            });
        },
        [getTablePreferences, persistPreferences]
    );

    const toggleTableColumnVisibility = useCallback(
        (tableKey, accessor, columns = []) => {
            if (!tableKey || !accessor) return;
            const current = getTablePreferences(tableKey, columns);
            persistPreferences(tableKey, {
                ...current,
                visibleColumns: {
                    ...current.visibleColumns,
                    [accessor]: !current.visibleColumns[accessor],
                },
            });
        },
        [getTablePreferences, persistPreferences]
    );

    const handleTableColumnDragEnd = useCallback(
        (tableKey, dragResult, columns = []) => {
            if (!tableKey || !dragResult?.destination) return;
            const current = getTablePreferences(tableKey, columns);
            const nextOrder = reorderVisibleTableColumnOrder(
                current.columnOrder,
                current.visibleColumns,
                dragResult.source.index,
                dragResult.destination.index
            );
            persistPreferences(tableKey, {
                ...current,
                columnOrder: nextOrder,
            });
        },
        [getTablePreferences, persistPreferences]
    );

    const resetTablePreferences = useCallback((tableKey, columns = []) => {
        if (!tableKey) return;
        const defaults = {
            columnOrder: buildDefaultTableColumnOrder(columns),
            visibleColumns: buildDefaultTableVisibleColumnsMap(columns),
        };
        persistPreferences(tableKey, defaults);
        return defaults;
    }, [persistPreferences]);

    const value = useMemo(
        () => ({
            preferencesByTableKey,
            syncTableColumnsFromDefinitions,
            getTablePreferences,
            setTableColumnOrder,
            setTableVisibleColumns,
            toggleTableColumnVisibility,
            handleTableColumnDragEnd,
            resetTablePreferences,
        }),
        [
            preferencesByTableKey,
            syncTableColumnsFromDefinitions,
            getTablePreferences,
            setTableColumnOrder,
            setTableVisibleColumns,
            toggleTableColumnVisibility,
            handleTableColumnDragEnd,
            resetTablePreferences,
        ]
    );

    return (
        <TableColumnPreferencesContext.Provider value={value}>
            {children}
        </TableColumnPreferencesContext.Provider>
    );
};

/**
 * Per-table column order + visibility (Context API), persisted via GeneralHelper storage helpers.
 * Re-syncs when metadata `columns` definition set changes (accessor / type signature).
 */
export const useTableColumnPreferences = (tableKey = "", columns = []) => {
    const context = useContext(TableColumnPreferencesContext);
    const resolvedTableKey = String(tableKey ?? "").trim();
    const normalizedColumns = useMemo(
        () => normalizeTableColumnDefinitionsForPreferences(columns),
        [columns]
    );
    const columnsSignature = useMemo(
        () => buildTableColumnsSignature(normalizedColumns),
        [normalizedColumns]
    );
    const lastSyncedSignatureRef = useRef("");

    useEffect(() => {
        if (!context || !resolvedTableKey || !columnsSignature) return;
        if (lastSyncedSignatureRef.current === columnsSignature) return;
        lastSyncedSignatureRef.current = columnsSignature;
        context.syncTableColumnsFromDefinitions(resolvedTableKey, normalizedColumns);
    }, [context, resolvedTableKey, columnsSignature, normalizedColumns]);

    const storedPrefs = context?.preferencesByTableKey?.[resolvedTableKey];

    const { columnOrder, visibleColumns, orderedColumns } = useMemo(() => {
        if (!context || !resolvedTableKey) {
            const fallback = resolveTableColumnPreferences(
                resolvedTableKey,
                normalizedColumns
            );
            return {
                columnOrder: fallback.columnOrder,
                visibleColumns: fallback.visibleColumns,
                orderedColumns: getOrderedTableColumnDefinitions(
                    normalizedColumns,
                    fallback.columnOrder
                ),
            };
        }
        const prefs = context.getTablePreferences(resolvedTableKey, normalizedColumns);
        return {
            columnOrder: prefs.columnOrder,
            visibleColumns: prefs.visibleColumns,
            orderedColumns: getOrderedTableColumnDefinitions(
                normalizedColumns,
                prefs.columnOrder
            ),
        };
    }, [context, resolvedTableKey, normalizedColumns, columnsSignature, storedPrefs]);

    const visibleColumnOrder = useMemo(
        () => getVisibleTableColumnOrder(columnOrder, visibleColumns),
        [columnOrder, visibleColumns]
    );

    if (!context) {
        return {
            columnOrder,
            visibleColumns,
            visibleColumnOrder,
            orderedColumns,
            handleColumnDragEnd: () => {},
            handleColumnToggle: () => {},
            setColumnOrder: () => {},
            setVisibleColumns: () => {},
            resetPreferences: () => {},
            syncWithColumnDefinitions: () => {},
        };
    }

    return {
        columnOrder,
        visibleColumns,
        visibleColumnOrder,
        orderedColumns,
        handleColumnDragEnd: (dragResult) =>
            context.handleTableColumnDragEnd(
                resolvedTableKey,
                dragResult,
                normalizedColumns
            ),
        handleColumnToggle: (preferenceKey) =>
            context.toggleTableColumnVisibility(
                resolvedTableKey,
                preferenceKey,
                normalizedColumns
            ),
        setColumnOrder: (order) =>
            context.setTableColumnOrder(resolvedTableKey, order, normalizedColumns),
        setVisibleColumns: (map) =>
            context.setTableVisibleColumns(resolvedTableKey, map, normalizedColumns),
        resetPreferences: () =>
            context.resetTablePreferences(resolvedTableKey, normalizedColumns),
        syncWithColumnDefinitions: () =>
            context.syncTableColumnsFromDefinitions(resolvedTableKey, normalizedColumns),
    };
};

export default TableColumnPreferencesContext;
